import { useEffect, useState } from "react";
import { Sparkles, X } from "lucide-react";
import { getSubscription, getCreatorId } from "../lib/api";

const DISMISS_KEY = "otaku_trial_reminder_dismissed";

// Friendly heads-up shown on the Catalogue when the user is in a Stripe free
// trial and the paid billing is <= 48h away — so nobody feels caught by
// surprise. Dismissible via sessionStorage; auto-hides once outside that
// window.
export default function TrialEndingBanner() {
  const [subscription, setSubscription] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try { if (window.sessionStorage.getItem(DISMISS_KEY) === "1") setDismissed(true); }
    catch (err) { console.warn("sessionStorage read failed:", err); }
    getSubscription(getCreatorId())
      .then((s) => setSubscription(s))
      .catch(() => setSubscription(null));
  }, []);

  const dismiss = () => {
    setDismissed(true);
    try { window.sessionStorage.setItem(DISMISS_KEY, "1"); }
    catch (err) { console.warn("sessionStorage write failed:", err); }
  };

  if (dismissed || !subscription) return null;
  if (subscription.state !== "trialing") return null;
  const expiresAt = subscription.expires_at;
  if (!expiresAt) return null;

  const msLeft = new Date(expiresAt).getTime() - Date.now();
  const daysLeft = msLeft / (1000 * 60 * 60 * 24);
  // Only show inside the last ~48h of a 7-day trial (i.e. day 6+).
  if (daysLeft > 2 || daysLeft < 0) return null;

  const chargeDay = new Date(expiresAt).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
  const roundedHours = Math.max(1, Math.round(msLeft / (1000 * 60 * 60)));
  const timeLeft = daysLeft >= 1
    ? `${Math.floor(daysLeft * 24 / 24) + 1} day${Math.floor(daysLeft) === 0 ? "" : "s"} left`
    : `${roundedHours} hours left`;

  return (
    <div
      data-testid="trial-ending-banner"
      className="mb-8 flex flex-col gap-3 border border-emerald-500/40 bg-emerald-500/5 p-4 md:flex-row md:items-center md:justify-between"
    >
      <div className="flex items-start gap-3">
        <Sparkles className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-400" />
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-400">
            Trial ending · {timeLeft}
          </p>
          <p className="mt-1 text-sm text-neutral-200">
            Your free trial converts to a paid Pro Pass on <span className="text-white">{chargeDay}</span>. Loving it? No action needed. Not for you? Cancel from Account before then and you won&apos;t be charged.
          </p>
        </div>
      </div>
      <div className="flex flex-shrink-0 items-center gap-2">
        <a
          href="/account"
          data-testid="trial-banner-manage"
          className="border border-white/20 bg-white px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-black hover:bg-emerald-400"
        >
          Manage plan
        </a>
        <button
          data-testid="trial-banner-dismiss"
          onClick={dismiss}
          aria-label="Dismiss"
          className="border border-white/15 p-2 text-neutral-400 hover:bg-white hover:text-black"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}
